import { cn } from "../../lib/utils";

interface LogoProps {
  /**
   * `full` renders the glyph + "WebTemplate" wordmark (default).
   * `mark` renders only the glyph — used by the collapsed sidebar.
   */
  variant?: "full" | "mark";
  className?: string;
}

/**
 * Brand logo for WebTemplate.
 *
 * Monochrome by design: the mark is drawn with `currentColor` so it inherits
 * the surrounding text color and works on any token background (light/dark,
 * sidebar, etc.). The glyph is an abstract "stacked layers / W" mark built from
 * geometric strokes — no gradients, no raster, no emoji.
 */
export function Logo({ variant = "full", className }: LogoProps) {
  const mark = (
    <svg
      viewBox="0 0 24 24"
      role="img"
      aria-label="WebTemplate"
      className="size-6 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <title>WebTemplate</title>
      {/* Top plate of the stacked-layers mark */}
      <path d="M12 3 4 7l8 4 8-4-8-4Z" />
      {/* Middle layer */}
      <path d="M4 12l8 4 8-4" />
      {/* Bottom layer */}
      <path d="M4 17l8 4 8-4" />
    </svg>
  );

  if (variant === "mark") {
    return (
      <span className={cn("text-foreground inline-flex", className)}>
        {mark}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "text-foreground inline-flex items-center gap-2",
        className,
      )}
    >
      {mark}
      <span className="text-base font-semibold tracking-tight">
        WebTemplate
      </span>
    </span>
  );
}

export type { LogoProps };
